import { BiSolidUserAccount } from "solid-icons/bi";
import { type Component, createMemo, createSignal, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { AvatarToDeprecate } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { AnonymizationUpsellModal } from "@/domains/company/anonymization/components/anonymization-upsell-modal";
import { useAnonymousCvAccess } from "@/domains/company/anonymization/use-anonymization-access";
import { PremiumLockBadge } from "@/domains/company/talent-pool/components/premium-lock-badge";
import { cn } from "@/libs/cn";
import { cvBarSuffix, cvSlotsLabelSuffix } from "./labels";
import { type CvSelection, MAX_CV_SELECTION } from "./types";
import type { useCvDownload } from "./useCvDownload";

/**
 * Barra flottante della selezione CV — gemella di
 * `FreelancerSelectionFloatingBar`: 4 slot avatar, click sull'avatar per
 * rimuovere, CTA a destra. La CTA non naviga: scarica i CV anonimi dei
 * candidati selezionati (un PDF se è uno solo, altrimenti uno ZIP — vedi
 * `useCvDownload`), lasciando la selezione al suo posto.
 *
 * Condivisa da ricerca attiva e talent pool: la selezione e le mutation
 * arrivano da fuori, così la UI è letteralmente la stessa sulle due superfici
 * mentre ognuna tiene il proprio stato e i propri endpoint.
 *
 * Chi la monta la mostra solo a selezione non vuota.
 */
export const CvSelectionFloatingBar: Component<{
  selection: CvSelection;
  download: ReturnType<typeof useCvDownload>;
  /** true quando un'altra barra occupa già `top-4`: scende sotto di essa. */
  stacked?: boolean;
}> = (props) => {
  const access = useAnonymousCvAccess();
  const [upsellOpen, setUpsellOpen] = createSignal(false);
  const { t } = useI18n();
  const [hoveredId, setHoveredId] = createSignal<string | null>(null);

  const slots = createMemo(() =>
    Array.from(
      { length: MAX_CV_SELECTION },
      (_, i) => props.selection.selected()[i] ?? null,
    ),
  );

  /**
   * A download riuscito la selezione si svuota e la barra sparisce: chi monta
   * la barra lo fa passando `onDownloaded` a `useCvDownload`. Scaricare CHIUDE
   * il giro — quei CV sono sul disco dell'utente — e tenere i candidati in
   * canna costringerebbe a sparecchiarli a mano prima del gruppo successivo.
   * Su errore invece restano, così riprovare non impone di ricomporre la lista.
   */
  const handleDownload = () => {
    // The selection is deliberately left untouched: meeting a paywall must not
    // cost the user the candidates they already picked.
    if (access() === "locked") {
      setUpsellOpen(true);
      return;
    }
    if (access() === "inert") return;
    props.download.download(props.selection.selected().map((f) => f.id));
  };

  /* Con un solo candidato si scarica un PDF, da due in su uno .zip — e
     l'etichetta lo dice, così si sa cosa si salva prima di cliccare. La regola
     del suffisso vive in `labels`. `as string` perché la chiave è un template
     literal e `t()` tornerebbe `unknown`. */
  const barKey = (base: string) =>
    t(
      `company.search.freelancers.result.cv_bar.${base}_${cvBarSuffix(
        props.selection.count(),
      )}`,
      { count: props.selection.count() },
    ) as string;

  const slotsLabel = () => {
    const remaining = props.selection.remaining();
    return t(
      `company.search.freelancers.result.cv_bar.slots_label_${cvSlotsLabelSuffix(
        remaining,
      )}`,
      { selected: props.selection.count(), remaining },
    ) as string;
  };

  return (
    <div
      class={cn(
        "fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 px-5 py-3 bg-white border border-gray-200 rounded-full shadow-lg",
        props.stacked ? "top-24" : "top-4",
      )}
    >
      {/* Left: label + slots info */}
      <div class="flex flex-col">
        <span class="text-xs font-semibold text-gray-800">
          {barKey("label")}
        </span>
        <span class="text-xs text-gray-500">{slotsLabel()}</span>
      </div>

      {/* Center: 4 avatar slots */}
      <div class="flex items-center gap-1.5">
        <For each={slots()}>
          {(candidate) => (
            <Show
              when={candidate}
              fallback={
                <div class="h-9 w-9 rounded-full border-2 border-dashed border-gray-300" />
              }
            >
              {(f) => (
                <button
                  type="button"
                  class="relative h-9 w-9"
                  onMouseEnter={() => setHoveredId(f().id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => props.selection.remove(f().id)}
                >
                  <AvatarToDeprecate
                    src={f().avatar_url ?? ""}
                    alt={f().full_name ?? ""}
                    class="h-9 w-9"
                    fallback={<BiSolidUserAccount class="w-5 h-5" />}
                  />
                  <Show when={hoveredId() === f().id}>
                    <span class="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 text-white text-base font-bold">
                      ×
                    </span>
                  </Show>
                </button>
              )}
            </Show>
          )}
        </For>
      </div>

      {/* Right: download. Nessun "annulla": togliere l'ultimo candidato dalla
          sua faccia svuota la selezione e fa sparire la barra, quindi un
          secondo comando per la stessa cosa era solo rumore. */}
      <Button
        size="sm"
        onClick={handleDownload}
        disabled={props.download.isPending()}
      >
        <Show when={access() === "locked"}>
          <PremiumLockBadge class="mr-1.5" />
        </Show>
        {props.download.isPending()
          ? t("company.search.freelancers.result.cv_bar.downloading")
          : `${barKey("download")} ›`}
      </Button>

      <AnonymizationUpsellModal
        open={upsellOpen()}
        onOpenChange={setUpsellOpen}
        feature="anonymous_cv"
        source="active_search_bar"
      />
    </div>
  );
};
